import cookies from 'js-cookie'
import { useCallback, useState } from 'react'

import { ERROR_AUTH_VERIFY_OTP, ERROR_AUTH_VERIFY_RECOVERY_CODE } from '@/app/module/auth/definitions'
import { setToken } from '@/app/module/auth/helpers'
import { getUserPreferences } from '@/app/module/auth/store/session.slice'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getUser, setPageLoadSource } from '@/app/module/user/store/actions'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export type OTPValues = {
  passcode: string
}

export type RecoveryCodeValues = {
  recovery: string
}

export const useVerifyOTP = () => {
  const dispatch = useAppDispatch()
  const [authTotp, mutationResult] = esApi.endpoints.authTotp.useMutation()
  const [isLoading, setIsLoading] = useState(false)

  const verifyOTP = useCallback(
    async (values: OTPValues | RecoveryCodeValues, token: string) => {
      const isOTP = 'passcode' in values
      setIsLoading(true)

      try {
        const body = isOTP
          ? {
              passcode: values.passcode,
            }
          : {
              recovery: values.recovery,
            }

        const response = await authTotp({
          body,
          authorization: `JWT ${token}`,
        }).unwrap()

        const { access: authToken, validUntil } = response

        // Remove OTP token and set main auth token
        cookies.remove('otpToken')

        if (authToken) {
          setToken(authToken, validUntil || '')
        }

        // Set page load source for tracking
        setPageLoadSource('login')

        // Get user and set up preferences
        const user = await dispatch(getUser({ token: authToken }))

        if (authToken && user && !(user instanceof Error)) {
          dispatch(getUserPreferences({ user, token: authToken }))
        }

        return { user, authToken }
      } catch (error) {
        const fallback = isOTP ? ERROR_AUTH_VERIFY_OTP : ERROR_AUTH_VERIFY_RECOVERY_CODE
        const errorMessage = getErrorMessage(error, fallback)
        dispatch(createNotification({ 'verify-OTP-error': { type: 'error', message: errorMessage } }))
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, authTotp],
  )

  return [verifyOTP, { ...mutationResult, isLoading }] as const
}
